/**
 * Görev Ekleri (Task Attachments) — tenant-wide read-only listing of
 * every file attached to any task.
 *
 * Design mirrors `CopFilesConstruction` (same page family, same
 * mental model): left rail groups by project → right pane shows
 * category chips (by MIME family) + card grid. This keeps Dosyalar
 * as ONE consistent surface regardless of which tab is active.
 *
 * Backend: GET /attachments/tenant returns each attachment enriched
 * with `task_id`, `task_title`, `project_id`, `project_name`.
 *
 * Interactions:
 *   - Card click / "Önizle" button → open blob in new tab (browser
 *     handles PDF/image viewers natively)
 *   - "İndir" saves file locally
 *   - Project chip → deep-link to the project's page with the task
 *     auto-opened via `?task=<id>`
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Search,
  Loader2,
  Download,
  Eye,
  FileText,
  Layers,
  ExternalLink,
  Image as ImageIcon,
  Film,
  Music,
  FileCode,
  File as FileIcon,
  Paperclip,
  ClipboardList,
  Trash2,
  X,
} from "lucide-react";
import { Input } from "../../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { api, formatApiErrorDetail } from "../../lib/api";
import { humanFileSize } from "../../lib/constructionFileMeta";

/** MIME → visual category. Same shape as `FILE_CATEGORIES` in
 *  constructionFileMeta so the chip visuals feel identical. */
const TA_CATEGORIES = [
  { key: "image",    label: "Resim",    Icon: ImageIcon, tint: "bg-rose-50 text-rose-700",     activeTint: "bg-rose-600 text-white" },
  { key: "pdf",      label: "PDF",      Icon: FileText,  tint: "bg-red-50 text-red-700",       activeTint: "bg-red-600 text-white" },
  { key: "document", label: "Doküman",  Icon: FileCode,  tint: "bg-sky-50 text-sky-700",       activeTint: "bg-sky-600 text-white" },
  { key: "video",    label: "Video",    Icon: Film,      tint: "bg-violet-50 text-violet-700", activeTint: "bg-violet-600 text-white" },
  { key: "audio",    label: "Ses",      Icon: Music,     tint: "bg-amber-50 text-amber-700",   activeTint: "bg-amber-600 text-white" },
  { key: "other",    label: "Diğer",    Icon: FileIcon,  tint: "bg-zinc-100 text-zinc-700",    activeTint: "bg-zinc-900 text-white" },
];
const TA_CATEGORY_META = Object.fromEntries(TA_CATEGORIES.map((c) => [c.key, c]));

function categoryOf(file) {
  const ct = (file.content_type || "").toLowerCase();
  const name = (file.original_filename || "").toLowerCase();
  if (ct.startsWith("image/")) return "image";
  if (ct === "application/pdf" || name.endsWith(".pdf")) return "pdf";
  if (ct.startsWith("video/")) return "video";
  if (ct.startsWith("audio/")) return "audio";
  if (
    ct.startsWith("text/") ||
    ct.includes("word") ||
    ct.includes("excel") ||
    ct.includes("spreadsheet") ||
    ct.includes("presentation") ||
    /\.(docx?|xlsx?|pptx?|csv|txt|md|json|xml)$/.test(name)
  ) {
    return "document";
  }
  return "other";
}

function formatRelative(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const diff = Math.max(0, Date.now() - t);
  const sec = Math.round(diff / 1000);
  if (sec < 45) return "az önce";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}dk`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}sa`;
  const d = Math.round(hr / 24);
  if (d < 7) return `${d}g`;
  const w = Math.round(d / 7);
  if (w < 5) return `${w}h`;
  const mo = Math.round(d / 30);
  if (mo < 12) return `${mo}ay`;
  return `${Math.round(d / 365)}y`;
}

/** Turn axios's Blob-typed error body into the real backend detail. */
async function parseBlobError(err) {
  const blob = err?.response?.data;
  if (blob instanceof Blob) {
    try {
      const text = await blob.text();
      try {
        const j = JSON.parse(text);
        return j.detail || j.message || text;
      } catch {
        return text || "Dosya indirilemedi";
      }
    } catch {
      return "Dosya indirilemedi";
    }
  }
  return formatApiErrorDetail(err?.response?.data?.detail) || "Dosya indirilemedi";
}

export default function TaskAttachmentsView() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projectFilter, setProjectFilter] = useState("all"); // project_id or 'all' or '__orphan__'
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [q, setQ] = useState("");
  const [thumbUrls, setThumbUrls] = useState({});
  // Inline preview modal — user asked (2026-02) that previews open in
  // a modal instead of a new browser tab so the app context isn't
  // interrupted. `preview` holds `{ file, url }`; null when closed.
  const [preview, setPreview] = useState(null);
  // Delete confirmation — `confirmDelete` holds the file about to be
  // removed (undo-safe: backend soft-deletes so the file is only
  // hidden, not physically purged).
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Bulk selection state — Set<attachment_id>.
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/attachments/tenant");
        setFiles(Array.isArray(data) ? data : []);
      } catch (err) {
        toast.error(
          formatApiErrorDetail(err.response?.data?.detail) ||
            "Görev ekleri yüklenemedi",
        );
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Roll-up: { project_id: { name, count, totalBytes } }. Attachments
  // whose task/project has been deleted land in a synthetic "__orphan__"
  // bucket so they're still findable.
  const projects = useMemo(() => {
    const m = new Map();
    for (const f of files) {
      const pid = f.project_id || "__orphan__";
      const pname =
        f.project_id && f.project_name && f.project_name !== "—"
          ? f.project_name
          : "Silinmiş / Bilinmeyen";
      const cur = m.get(pid) || {
        id: pid,
        name: pname,
        count: 0,
        totalBytes: 0,
        orphan: pid === "__orphan__",
      };
      cur.count += 1;
      cur.totalBytes += f.size || 0;
      m.set(pid, cur);
    }
    return Array.from(m.values()).sort((a, b) => {
      if (a.orphan) return 1;
      if (b.orphan) return -1;
      return a.name.localeCompare(b.name, "tr");
    });
  }, [files]);

  const totalBytesAll = useMemo(
    () => files.reduce((s, f) => s + (f.size || 0), 0),
    [files],
  );

  // Category counts respect the project filter so the chips reflect
  // exactly what's in scope on the right.
  const projectScopedFiles = useMemo(() => {
    if (projectFilter === "all") return files;
    if (projectFilter === "__orphan__") return files.filter((f) => !f.project_id);
    return files.filter((f) => f.project_id === projectFilter);
  }, [files, projectFilter]);

  const categoryCounts = useMemo(() => {
    const counts = { all: projectScopedFiles.length };
    for (const c of TA_CATEGORIES) counts[c.key] = 0;
    for (const f of projectScopedFiles) counts[categoryOf(f)] += 1;
    return counts;
  }, [projectScopedFiles]);

  // Final filtered list — project + category + search.
  const shown = useMemo(() => {
    let list = projectScopedFiles;
    if (categoryFilter !== "all") {
      list = list.filter((f) => categoryOf(f) === categoryFilter);
    }
    const qq = q.trim().toLocaleLowerCase("tr");
    if (qq) {
      list = list.filter(
        (r) =>
          (r.original_filename || "").toLocaleLowerCase("tr").includes(qq) ||
          (r.task_title || "").toLocaleLowerCase("tr").includes(qq) ||
          (r.project_name || "").toLocaleLowerCase("tr").includes(qq),
      );
    }
    return list;
  }, [projectScopedFiles, categoryFilter, q]);

  // Fetch image thumbnails in batches; failed fetches are marked with
  // a `null` sentinel so they fall back to the type icon (no retry loop).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const missing = shown.filter(
        (f) =>
          (f.content_type || "").startsWith("image/") && !(f.id in thumbUrls),
      );
      for (const f of missing.slice(0, 20)) {
        try {
          const res = await api.get(`/attachments/${f.id}/download`, {
            responseType: "blob",
          });
          if (cancelled) return;
          const url = URL.createObjectURL(res.data);
          setThumbUrls((prev) => ({ ...prev, [f.id]: url }));
        } catch {
          if (cancelled) return;
          setThumbUrls((prev) => ({ ...prev, [f.id]: null }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shown, thumbUrls]);

  // Revoke any object URLs on unmount.
  useEffect(() => {
    return () => {
      Object.values(thumbUrls).forEach((u) => URL.revokeObjectURL(u));
    };
  }, []);

  // Filtre değişince bayat seçim kalmasın.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [projectFilter, categoryFilter, q]);

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }, []);

  const saveBlob = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 200);
  };

  const bulkDownload = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      const res = await api.post(
        "/attachments/bulk-download",
        { file_ids: ids },
        { responseType: "blob" },
      );
      saveBlob(res.data, "serpix-gorev-ekleri.zip");
      const skipped = Number(res.headers?.["x-skipped-count"] || 0);
      if (skipped > 0) toast.warning(`${skipped} dosya atlandı (bulunamadı)`);
      else toast.success(`${ids.length} dosya ZIP olarak indirildi`);
    } catch {
      toast.error("Toplu indirme başarısız");
    } finally {
      setBulkBusy(false);
    }
  }, [selectedIds]);

  const bulkDelete = useCallback(async () => {
    const ids = Array.from(selectedIds);
    setBulkBusy(true);
    try {
      const { data } = await api.post("/attachments/bulk-delete", {
        file_ids: ids,
      });
      setFiles((cur) => cur.filter((x) => !selectedIds.has(x.id)));
      setThumbUrls((cur) => {
        const next = { ...cur };
        ids.forEach((id) => {
          if (next[id]) URL.revokeObjectURL(next[id]);
          delete next[id];
        });
        return next;
      });
      toast.success(`${data.deleted ?? ids.length} dosya silindi`);
      setSelectedIds(new Set());
      setConfirmBulkDelete(false);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Silinemedi",
      );
    } finally {
      setBulkBusy(false);
    }
  }, [selectedIds]);

  const fetchBlob = useCallback(async (attId) => {
    try {
      const res = await api.get(`/attachments/${attId}/download`, {
        responseType: "blob",
      });
      return res.data;
    } catch (err) {
      throw new Error(await parseBlobError(err));
    }
  }, []);

  const downloadFile = useCallback(
    async (f) => {
      try {
        const blob = await fetchBlob(f.id);
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = f.original_filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 200);
      } catch (err) {
        toast.error(err.message);
      }
    },
    [fetchBlob],
  );

  const previewFile = useCallback(
    async (f) => {
      // Open the modal IMMEDIATELY with a spinner — big files take a
      // while to download and a silent wait feels like a dead click.
      setPreview({ file: f, url: null });
      try {
        const blob = await fetchBlob(f.id);
        const typed = new Blob([blob], {
          type: f.content_type || "application/octet-stream",
        });
        const url = URL.createObjectURL(typed);
        setPreview((cur) => {
          if (!cur || cur.file.id !== f.id) {
            URL.revokeObjectURL(url);
            return cur;
          }
          return { file: f, url };
        });
      } catch (err) {
        setPreview((cur) => (cur && cur.file.id === f.id ? null : cur));
        toast.error(err.message);
      }
    },
    [fetchBlob],
  );

  const closePreview = useCallback(() => {
    setPreview((cur) => {
      if (cur?.url) URL.revokeObjectURL(cur.url);
      return null;
    });
  }, []);

  const deleteFile = useCallback(async () => {
    const f = confirmDelete;
    if (!f) return;
    setDeleting(true);
    try {
      await api.delete(`/attachments/${f.id}`);
      // Optimistic remove — server soft-deletes; if it fails we
      // restore the list on the catch branch.
      setFiles((cur) => cur.filter((x) => x.id !== f.id));
      // Also drop the cached thumbnail URL.
      setThumbUrls((cur) => {
        const next = { ...cur };
        if (next[f.id]) URL.revokeObjectURL(next[f.id]);
        delete next[f.id];
        return next;
      });
      toast.success("Dosya silindi");
      setConfirmDelete(null);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Silinemedi",
      );
    } finally {
      setDeleting(false);
    }
  }, [confirmDelete]);

  return (
    <div
      className="grid grid-cols-1 gap-4 md:grid-cols-[260px_1fr]"
      data-testid="task-attachments-page"
    >
      {/* --- Left rail: project navigator --- */}
      <aside
        className="flex max-h-[70vh] flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white"
        data-testid="ta-project-rail"
      >
        <div className="border-b border-zinc-100 px-3 py-2.5">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
            <Layers className="h-3.5 w-3.5" /> Projeler
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          <ProjectRailButton
            active={projectFilter === "all"}
            onClick={() => setProjectFilter("all")}
            label="Tüm projeler"
            sub={files.length > 0 ? humanFileSize(totalBytesAll) : ""}
            count={files.length}
            testid="ta-project-all"
          />
          <div className="my-1 h-px bg-zinc-100" />
          {projects.length === 0 ? (
            <div className="px-3 py-6 text-center text-[11px] text-zinc-400">
              Henüz görev eki yok.
            </div>
          ) : (
            projects.map((p) => {
              const parts = [];
              if (p.totalBytes > 0) parts.push(humanFileSize(p.totalBytes));
              return (
                <ProjectRailButton
                  key={p.id}
                  active={projectFilter === p.id}
                  onClick={() => setProjectFilter(p.id)}
                  label={p.name}
                  sub={parts.join(" · ")}
                  count={p.count}
                  muted={p.orphan}
                  testid={`ta-project-${p.id}`}
                />
              );
            })
          )}
        </div>
      </aside>

      {/* --- Right main content --- */}
      <section className="min-w-0 space-y-4">
        {/* Search + category chips */}
        <div className="space-y-3 rounded-xl border border-zinc-200 bg-white p-3">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Dosya, görev veya proje ara…"
                className="h-9 pl-8 text-sm"
                data-testid="ta-search"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <CategoryChip
              active={categoryFilter === "all"}
              onClick={() => setCategoryFilter("all")}
              Icon={Paperclip}
              label="Tümü"
              count={categoryCounts.all}
              testid="ta-category-all"
              tint="bg-zinc-900 text-white"
              inactiveTint="bg-zinc-100 text-zinc-700"
            />
            {TA_CATEGORIES.map((c) => (
              <CategoryChip
                key={c.key}
                active={categoryFilter === c.key}
                onClick={() => setCategoryFilter(c.key)}
                Icon={c.Icon}
                label={c.label}
                count={categoryCounts[c.key] || 0}
                testid={`ta-category-${c.key}`}
                tint={c.activeTint}
                inactiveTint={c.tint}
              />
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-sm text-zinc-500">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Yükleniyor…
          </div>
        ) : shown.length === 0 ? (
          <div className="rounded-xl border border-dashed border-zinc-200 bg-zinc-50 py-16 text-center text-sm text-zinc-500">
            {files.length === 0
              ? "Henüz hiçbir göreve dosya eklenmemiş."
              : "Filtrelerle eşleşen dosya yok."}
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <div className="text-[12px] text-zinc-500">
                {shown.length} dosya gösteriliyor
              </div>
            </div>
            <ul
              className="divide-y divide-zinc-100 overflow-hidden rounded-xl border border-zinc-200 bg-white"
              data-testid="ta-files-list"
            >
              {shown.map((f) => (
                <TaskFileCard
                  key={f.id}
                  file={f}
                  thumbUrl={thumbUrls[f.id]}
                  onDownload={() => downloadFile(f)}
                  onPreview={() => previewFile(f)}
                  onDelete={() => setConfirmDelete(f)}
                  selected={selectedIds.has(f.id)}
                  onToggleSelect={() => toggleSelect(f.id)}
                />
              ))}
            </ul>
            {selectedIds.size > 0 && (
              <div
                className="sticky bottom-3 mt-3 flex flex-wrap items-center gap-2 rounded-full border border-zinc-200 bg-white p-1.5 pl-4 shadow-lg"
                data-testid="ta-bulk-bar"
              >
                <div className="mr-1 text-sm font-medium text-zinc-800">
                  {selectedIds.size} dosya seçildi
                </div>
                <Button
                  size="sm"
                  onClick={bulkDownload}
                  disabled={bulkBusy}
                  data-testid="ta-bulk-download-btn"
                  className="gap-1.5 bg-violet-600 text-white hover:bg-violet-700"
                >
                  {bulkBusy ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Download className="h-3.5 w-3.5" />
                  )}
                  İndir (ZIP)
                </Button>
                <Button
                  size="sm"
                  onClick={() => setConfirmBulkDelete(true)}
                  disabled={bulkBusy}
                  data-testid="ta-bulk-delete-btn"
                  className="gap-1.5 bg-rose-600 text-white hover:bg-rose-700"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Sil
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelectedIds(new Set())}
                  data-testid="ta-bulk-cancel-btn"
                  className="gap-1.5"
                >
                  <X className="h-3.5 w-3.5" />
                  İptal
                </Button>
              </div>
            )}
          </>
        )}
      </section>

      {/* Inline preview modal — image / PDF / anything the browser can
          render inside an iframe. Falls back to a download prompt for
          binary types (video/office docs). */}
      <Dialog
        open={!!preview}
        onOpenChange={(o) => {
          if (!o) closePreview();
        }}
      >
        <DialogContent
          data-testid="ta-preview-modal"
          className="max-h-[92vh] w-[min(96vw,1100px)] max-w-none overflow-hidden p-0 sm:rounded-xl"
        >
          <DialogHeader className="flex flex-row items-center gap-3 border-b border-zinc-100 bg-zinc-50 px-4 py-3">
            <div className="flex min-w-0 flex-1 flex-col">
              <DialogTitle
                className="truncate text-sm font-semibold text-zinc-900"
                title={preview?.file?.original_filename}
              >
                {preview?.file?.original_filename}
              </DialogTitle>
              <div className="text-[11px] text-zinc-500">
                {preview?.file
                  ? `${(preview.file.content_type || "").split("/")[1] || "dosya"} · ${humanFileSize(preview.file.size)}`
                  : ""}
              </div>
            </div>
            <button
              type="button"
              onClick={() => preview && downloadFile(preview.file)}
              className="rounded-md border border-zinc-200 bg-white p-1.5 text-zinc-600 hover:bg-zinc-100"
              title="İndir"
              data-testid="ta-preview-modal-download"
            >
              <Download className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={closePreview}
              className="rounded-md border border-zinc-200 bg-white p-1.5 text-zinc-600 hover:bg-zinc-100"
              title="Kapat"
              data-testid="ta-preview-modal-close"
            >
              <X className="h-4 w-4" />
            </button>
          </DialogHeader>
          <div className="flex max-h-[calc(92vh-60px)] items-center justify-center bg-zinc-100">
            <PreviewBody file={preview?.file} url={preview?.url} />
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation — soft-delete on server (attachment.is_deleted). */}
      <Dialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && !deleting && setConfirmDelete(null)}
      >
        <DialogContent
          data-testid="ta-delete-dialog"
          className="max-w-sm"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <Trash2 className="h-4 w-4" /> Dosyayı sil
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm text-zinc-700">
            <p>
              <b className="break-all">
                {confirmDelete?.original_filename}
              </b>{" "}
              silinecek.
            </p>
            <p className="text-xs text-zinc-500">
              Bu işlem geri alınamaz. Bağlı görev hâlâ varsa görevin
              ekleri arasında da kaybolur.
            </p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmDelete(null)}
              disabled={deleting}
              data-testid="ta-delete-cancel"
            >
              Vazgeç
            </Button>
            <Button
              type="button"
              onClick={deleteFile}
              disabled={deleting}
              data-testid="ta-delete-confirm"
              className="bg-rose-600 text-white hover:bg-rose-700"
            >
              {deleting ? "Siliniyor..." : "Evet, sil"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk delete confirmation */}
      <Dialog
        open={confirmBulkDelete}
        onOpenChange={(o) => !o && !bulkBusy && setConfirmBulkDelete(false)}
      >
        <DialogContent data-testid="ta-bulk-delete-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <Trash2 className="h-4 w-4" /> {selectedIds.size} dosyayı sil
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-700">
            Seçilen dosyalar silinecek. Bağlı görevlerin ekleri arasından da
            kaldırılır. Bu işlem geri alınamaz.
          </p>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmBulkDelete(false)}
              disabled={bulkBusy}
              data-testid="ta-bulk-delete-cancel"
            >
              Vazgeç
            </Button>
            <Button
              type="button"
              onClick={bulkDelete}
              disabled={bulkBusy}
              data-testid="ta-bulk-delete-confirm"
              className="bg-rose-600 text-white hover:bg-rose-700"
            >
              {bulkBusy ? "Siliniyor..." : "Evet, sil"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Body of the preview modal — routes on the MIME type to render as
 *  an image, PDF iframe, or a generic download-cta card. */
function PreviewBody({ file, url }) {
  if (!file) return null;
  if (!url) {
    return (
      <div className="flex w-full items-center justify-center p-20 text-zinc-400">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Yükleniyor…
      </div>
    );
  }
  const ct = (file.content_type || "").toLowerCase();
  if (ct.startsWith("image/")) {
    return (
      <img
        src={url}
        alt={file.original_filename}
        className="max-h-[calc(92vh-60px)] max-w-full object-contain"
        data-testid="ta-preview-image"
      />
    );
  }
  if (ct === "application/pdf" || ct.startsWith("text/")) {
    return (
      <iframe
        src={url}
        title={file.original_filename}
        className="h-[calc(92vh-60px)] w-full border-0 bg-white"
        data-testid="ta-preview-pdf"
      />
    );
  }
  if (ct.startsWith("video/")) {
    return (
      <video
        src={url}
        controls
        className="max-h-[calc(92vh-60px)] max-w-full"
        data-testid="ta-preview-video"
      />
    );
  }
  if (ct.startsWith("audio/")) {
    return (
      <div className="flex w-full items-center justify-center p-10">
        <audio src={url} controls data-testid="ta-preview-audio" />
      </div>
    );
  }
  // Fallback — no inline renderer. Prompt download.
  return (
    <div className="flex w-full flex-col items-center justify-center gap-3 p-16 text-center">
      <FileIcon className="h-10 w-10 text-zinc-400" />
      <div className="text-sm text-zinc-700">
        Bu dosya tarayıcıda önizlenemiyor.
      </div>
      <Button
        type="button"
        asChild
        className="bg-zinc-900 text-white hover:bg-zinc-800"
      >
        <a href={url} download={file.original_filename}>
          <Download className="mr-2 h-3.5 w-3.5" /> İndir
        </a>
      </Button>
    </div>
  );
}

/** Left rail item — mirrors ProjectRailButton in CopFilesConstruction
 *  visually (same padding, radius, active-state colors). */
function ProjectRailButton({ active, onClick, label, sub, count, muted, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[13px] transition-colors ${
        active
          ? "bg-zinc-900 text-white"
          : muted
            ? "text-zinc-500 italic hover:bg-zinc-50"
            : "text-zinc-700 hover:bg-zinc-50"
      }`}
    >
      <div className="min-w-0 flex-1">
        <div className="truncate">{label}</div>
        {sub && (
          <div
            className={`truncate text-[10px] ${
              active ? "text-white/70" : "text-zinc-400"
            }`}
          >
            {sub}
          </div>
        )}
      </div>
      <span
        className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ${
          active ? "bg-white/15 text-white" : "bg-zinc-100 text-zinc-600"
        }`}
      >
        {count}
      </span>
    </button>
  );
}

/** Category chip — visually identical to CopFilesConstruction's chip. */
function CategoryChip({ active, onClick, Icon, label, count, testid, tint, inactiveTint }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-medium transition-colors ${
        active ? tint : `${inactiveTint} hover:brightness-95`
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
      <span
        className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ${
          active ? "bg-white/20 text-white" : "bg-white/60 text-zinc-800"
        }`}
      >
        {count}
      </span>
    </button>
  );
}

/** Compact task-attachment row — the WHOLE row opens the preview
 *  modal; download/delete icons stay pinned (and visible) on the
 *  right so long lists stay scannable. */
function TaskFileCard({
  file,
  thumbUrl,
  onDownload,
  onPreview,
  onDelete,
  selected,
  onToggleSelect,
}) {
  const cat = TA_CATEGORY_META[categoryOf(file)] || TA_CATEGORY_META.other;
  const Icon = cat.Icon;
  const isImage = (file.content_type || "").startsWith("image/");
  const hasTask = !!(file.task_id && file.task_title && file.task_title !== "—");
  const hasProject = !!(file.project_id && file.project_name && file.project_name !== "—");

  return (
    <li
      data-testid={`ta-card-${file.id}`}
      onClick={onPreview}
      className={`group flex cursor-pointer items-center gap-3 px-3 py-2 transition-colors ${
        selected ? "bg-violet-50/70" : "hover:bg-violet-50/50"
      }`}
      title="Önizlemek için tıklayın"
    >
      <span
        onClick={(e) => e.stopPropagation()}
        className="flex shrink-0 items-center"
      >
        <Checkbox
          checked={!!selected}
          onCheckedChange={onToggleSelect}
          data-testid={`ta-select-${file.id}`}
        />
      </span>
      {/* Mini thumbnail / type icon */}
      <div
        className={`flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg ${
          isImage && thumbUrl ? "bg-zinc-100" : cat.tint
        }`}
      >
        {isImage && thumbUrl ? (
          <img
            src={thumbUrl}
            alt={file.original_filename}
            className="h-full w-full object-cover"
          />
        ) : isImage && thumbUrl === undefined ? (
          <Loader2 className="h-4 w-4 animate-spin text-zinc-300" />
        ) : (
          <Icon className="h-[18px] w-[18px]" />
        )}
      </div>

      {/* Name + meta */}
      <div className="min-w-0 flex-1">
        <div
          className="truncate text-[13px] font-medium text-zinc-900"
          title={file.original_filename}
        >
          {file.original_filename}
        </div>
        <div className="mt-0.5 flex min-w-0 items-center gap-x-1.5 text-[11px] text-zinc-500">
          <span className="shrink-0 font-medium">{cat.label}</span>
          <span className="shrink-0">·</span>
          <span className="shrink-0">{humanFileSize(file.size)}</span>
          <span className="shrink-0">·</span>
          <span className="shrink-0">{formatRelative(file.created_at)}</span>
          {hasTask && (
            <>
              <span className="shrink-0">·</span>
              <span
                className="inline-flex min-w-0 items-center gap-1 truncate"
                title={file.task_title}
              >
                <ClipboardList className="h-3 w-3 shrink-0 text-zinc-400" />
                <span className="truncate">{file.task_title}</span>
              </span>
            </>
          )}
        </div>
      </div>

      {/* Project chip */}
      {hasProject ? (
        <Link
          to={`/app/projects/${file.project_id}${file.task_id ? `?task=${file.task_id}` : ""}`}
          onClick={(e) => e.stopPropagation()}
          data-testid={`ta-project-link-${file.id}`}
          className="hidden max-w-[180px] shrink-0 items-center gap-1 truncate rounded-md bg-zinc-100 px-2 py-0.5 text-[11px] font-medium text-zinc-800 hover:bg-violet-50 hover:text-violet-700 sm:inline-flex"
        >
          <span className="truncate">{file.project_name}</span>
          <ExternalLink className="ml-0.5 h-3 w-3 shrink-0 opacity-50" />
        </Link>
      ) : (
        <span className="hidden shrink-0 rounded-md bg-zinc-50 px-2 py-0.5 text-[11px] italic text-zinc-400 sm:inline-flex">
          Görev silinmiş
        </span>
      )}

      {/* Actions — always visible, pinned right */}
      <div className="flex shrink-0 items-center gap-0.5">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onPreview();
          }}
          data-testid={`ta-preview-btn-${file.id}`}
          title="Önizle"
          className="rounded-md p-1.5 text-zinc-400 transition-colors hover:bg-violet-100 hover:text-violet-700"
        >
          <Eye className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDownload();
          }}
          data-testid={`ta-download-${file.id}`}
          title="İndir"
          className="rounded-md p-1.5 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-900"
        >
          <Download className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          data-testid={`ta-delete-${file.id}`}
          title="Sil"
          className="rounded-md p-1.5 text-rose-500 transition-colors hover:bg-rose-50 hover:text-rose-700"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </li>
  );
}
